import React, { useMemo, useState } from "react";

const isValidYear = (value) =>
  typeof value === "number" && Number.isFinite(value);

const clampYear = (value, min, max) => Math.min(Math.max(value, min), max);

const HistoryRangeBar = ({
  active = false,
  range,
  selection,
  loading = false,
  onChange,
  onCommit,
  t,
}) => {
  const [activeThumb, setActiveThumb] = useState("from");
  const minYear = isValidYear(range?.minYear) ? range.minYear : null;
  const maxYear = isValidYear(range?.maxYear) ? range.maxYear : null;
  const hasRange = isValidYear(minYear) && isValidYear(maxYear);
  const hasSelection =
    isValidYear(selection?.from) && isValidYear(selection?.to);

  const fromValue = hasSelection ? selection.from : minYear;
  const toValue = hasSelection ? selection.to : maxYear;

  const normalizedFrom =
    hasRange && isValidYear(fromValue)
      ? clampYear(fromValue, minYear, maxYear)
      : null;
  const normalizedTo =
    hasRange && isValidYear(toValue) ? clampYear(toValue, minYear, maxYear) : null;

  const rangePercent = useMemo(() => {
    if (!hasRange || !isValidYear(normalizedFrom) || !isValidYear(normalizedTo)) {
      return { fromPct: 0, toPct: 0 };
    }
    const total = maxYear - minYear || 1;
    const fromPct = ((normalizedFrom - minYear) / total) * 100;
    const toPct = ((normalizedTo - minYear) / total) * 100;
    return {
      fromPct: Math.min(Math.max(fromPct, 0), 100),
      toPct: Math.min(Math.max(toPct, 0), 100),
    };
  }, [hasRange, maxYear, minYear, normalizedFrom, normalizedTo]);

  const labelFrom = t?.("yearFrom") || "From";
  const labelTo = t?.("yearTo") || "To";
  const loadingLabel = t?.("loading") || "Loading";
  const unavailableLabel = t?.("historyUnavailable") || "Range unavailable";

  const showControls =
    !loading &&
    hasRange &&
    isValidYear(normalizedFrom) &&
    isValidYear(normalizedTo);

  const commit = () => {
    if (onCommit) onCommit();
  };

  if (!active) return null;

  return (
    <div className="history-range-bar">
      <div className="history-range-bar__inner">
        {loading ? (
          <div className="history-range-bar__status">{loadingLabel}</div>
        ) : null}
        {!loading && !showControls ? (
          <div className="history-range-bar__status">{unavailableLabel}</div>
        ) : null}
        {!loading && showControls ? (
          <>
            <div className="history-range-bar__values">
              <span>
                {normalizedFrom} - {normalizedTo}
              </span>
            </div>
            <div className="history-range">
              <div className="history-range__track">
                <div
                  className="history-range__fill"
                  style={{
                    left: `${Math.min(rangePercent.fromPct, rangePercent.toPct)}%`,
                    width: `${Math.abs(rangePercent.toPct - rangePercent.fromPct)}%`,
                  }}
                />
              </div>
              <input
                type="range"
                min={minYear}
                max={maxYear}
                step={1}
                value={normalizedFrom}
                aria-label={labelFrom}
                className="history-range__input"
                style={{ zIndex: activeThumb === "from" ? 3 : 2 }}
                onPointerDown={() => setActiveThumb("from")}
                onChange={(event) =>
                  onChange && onChange({ from: Number(event.target.value) })
                }
                onMouseUp={commit}
                onTouchEnd={commit}
                onKeyUp={commit}
              />
              <input
                type="range"
                min={minYear}
                max={maxYear}
                step={1}
                value={normalizedTo}
                aria-label={labelTo}
                className="history-range__input"
                style={{ zIndex: activeThumb === "to" ? 3 : 2 }}
                onPointerDown={() => setActiveThumb("to")}
                onChange={(event) =>
                  onChange && onChange({ to: Number(event.target.value) })
                }
                onMouseUp={commit}
                onTouchEnd={commit}
                onKeyUp={commit}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default HistoryRangeBar;
